import React from 'react';
import EmptyState from '../shared/EmptyState';
import ModalPortal from '../../../../components/ModalPortal';

/**
 * AssignRootAdminModal - Modal for assigning a root administrator to an organization
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Called when the modal should close
 * @param {Object} props.organization - The organization to assign a root admin to
 * @param {string} props.assignRootAdminUserId - The currently selected user ID
 * @param {Function} props.setAssignRootAdminUserId - Called when the user ID changes
 * @param {string} props.assignRootAdminSearch - Search term for user autocomplete
 * @param {Function} props.setAssignRootAdminSearch - Called when search changes
 * @param {boolean} props.isAssignRootAdminDropdownOpen - Whether dropdown is open
 * @param {Function} props.setIsAssignRootAdminDropdownOpen - Called when dropdown toggle
 * @param {string} props.assignRootAdminError - Error message to display
 * @param {Function} props.setAssignRootAdminError - Called when error changes
 * @param {boolean} props.assignRootAdminSaving - Whether the assignment is being saved
 * @param {Function} props.onSubmit - Called when the form is submitted
 * @param {Array} props.users - Array of user objects for autocomplete
 * @param {Array} props.filteredAssignRootAdminOptions - Filtered users for autocomplete
 * @returns {React.ReactElement} Assign root admin modal component
 */
const AssignRootAdminModal = ({
  isOpen,
  onClose,
  organization,
  assignRootAdminUserId,
  setAssignRootAdminUserId,
  assignRootAdminSearch,
  setAssignRootAdminSearch,
  isAssignRootAdminDropdownOpen,
  setIsAssignRootAdminDropdownOpen,
  assignRootAdminError,
  setAssignRootAdminError,
  assignRootAdminSaving,
  onSubmit,
  users,
  filteredAssignRootAdminOptions
}) => {
  const overlayMouseDown = React.useRef(false);

  if (!isOpen) return null;

  return (
    <ModalPortal>
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) overlayMouseDown.current = true;
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && overlayMouseDown.current) {
          onClose();
        }
        overlayMouseDown.current = false;
      }}
    >
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '450px' }}>
        <h2 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0', fontSize: '1.25rem' }}>Assign Root Administrator</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Assign a root administrator for the organization <strong>{organization?.name || organization?.id}</strong>.
        </p>
        
        {assignRootAdminError && (
          <div style={{ color: 'var(--error)', fontSize: '0.85rem', padding: '0.75rem', background: 'rgba(220, 38, 38, 0.08)', borderRadius: 'var(--radius)', border: '1px solid rgba(220, 38, 38, 0.2)', marginBottom: '1rem' }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '0.5rem' }}></i>
            {assignRootAdminError}
          </div>
        )}

        <form onSubmit={onSubmit} className="importer-form">
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Select User</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <i className="fa-solid fa-user-shield" style={{ position: 'absolute', left: '1rem', color: 'var(--text-secondary)' }}></i>
              <input 
                  type="text"
                  className="input-field"
                  value={assignRootAdminSearch}
                  onChange={e => {
                    setAssignRootAdminSearch(e.target.value);
                    setIsAssignRootAdminDropdownOpen(true);
                    if (assignRootAdminUserId) {
                      setAssignRootAdminUserId('');
                    }
                  }}
                  onFocus={() => setIsAssignRootAdminDropdownOpen(true)}
                  placeholder="Type to search and select user..."
                  style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem', margin: 0, width: '100%', boxSizing: 'border-box' }}
              />
              {assignRootAdminUserId && (
                <button 
                    type="button"
                    onClick={() => {
                      setAssignRootAdminUserId('');
                      setAssignRootAdminSearch('');
                    }}
                    style={{ position: 'absolute', right: '1rem', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>

            {/* Autocomplete Dropdown */}
            {isAssignRootAdminDropdownOpen && (
              <>
                <div 
                    onClick={() => setIsAssignRootAdminDropdownOpen(false)} 
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 101, background: 'transparent' }} 
                />
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    zIndex: 102,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                }}>
                  {filteredAssignRootAdminOptions.length === 0 ? (
                    <EmptyState message="No matching users found." variant="inline" />
                  ) : (
                    filteredAssignRootAdminOptions.map(u => (
                      <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setAssignRootAdminUserId(u.id);
                            setAssignRootAdminSearch(u.fullName ? `${u.fullName} (${u.email})` : u.email);
                            setIsAssignRootAdminDropdownOpen(false);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 'calc(var(--radius) - 4px)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            padding: '0.5rem 0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            textAlign: 'left',
                            width: '100%',
                            transition: 'var(--transition)'
                          }}
                          className="table-row-hover"
                      >
                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{u.fullName || 'No Name'}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{u.email}</span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <div className="modal-actions" style={{ marginTop: '2rem' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={assignRootAdminSaving}>Cancel</button>
            <button type="submit" className="btn-primary" style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }} disabled={assignRootAdminSaving || !assignRootAdminUserId}>
              {assignRootAdminSaving ? 'Saving...' : 'Assign Administrator'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
};

export default AssignRootAdminModal;
